import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { UomSelect } from '@/components/inventory/UomSelect';
import { BomQtyPreview } from '@/components/manufacturing/BomQtyPreview';
import type { Item } from '@/features/inventory/hooks';
import { toProducedQty } from '@/lib/uom';
import {
  ancestorIds,
  useUpdateBomLine,
  type BomGraph,
  type BomLine,
} from '@/features/manufacturing/boms';

interface EditBomLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: BomLine | null;
  /** Assembly that owns the line. */
  assembly: { itemId: string; name: string; sku: string } | null;
  graph: BomGraph | undefined;
  /** Shown when the line lives inside a shared sub-assembly. */
  notice?: string;
}

export function EditBomLineDialog({
  open,
  onOpenChange,
  line,
  assembly,
  graph,
  notice,
}: EditBomLineDialogProps) {
  const update = useUpdateBomLine();
  const [componentId, setComponentId] = useState<string | null>(null);
  const [producedUom, setProducedUom] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState('each');
  const [scrap, setScrap] = useState('0');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && line) {
      setComponentId(line.component_item_id);
      setProducedUom(line.item?.base_uom ?? null);
      setQuantity(String(line.quantity));
      setUom(line.item?.base_uom ?? line.uom);
      setScrap(String(line.scrap_pct ?? 0));
      setNotes(line.notes ?? '');
    }
  }, [open, line]);

  const siblingIds = useMemo(() => {
    if (!assembly) return new Set<string>();
    return new Set(
      (graph?.byItemId.get(assembly.itemId)?.lines ?? [])
        .filter((l) => l.id !== line?.id)
        .map((l) => l.component_item_id),
    );
  }, [graph, assembly, line?.id]);

  const blocked = useMemo(() => {
    if (!assembly) return new Set<string>();
    const set = ancestorIds(graph, assembly.itemId);
    set.add(assembly.itemId);
    return set;
  }, [graph, assembly]);

  const pickerFilter = useMemo(
    () => (it: Item) => !blocked.has(it.id) && !siblingIds.has(it.id),
    [blocked, siblingIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!line || !componentId) return;
    const produced = toProducedQty(Number(quantity), uom, producedUom);
    try {
      await update.mutateAsync({
        id: line.id,
        componentItemId: componentId !== line.component_item_id ? componentId : undefined,
        quantity: produced.quantity,
        uom: produced.uom,
        scrapPct: Number(scrap || 0),
        notes,
      });
      toast.success('Line updated');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the line.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Edit BOM line</DialogTitle>
            <DialogDescription>
              On <span className="font-medium text-foreground">{assembly?.name}</span>{' '}
              <span className="font-mono text-xs">({assembly?.sku})</span>
            </DialogDescription>
          </DialogHeader>

          {notice && (
            <p className="rounded-md border border-fresh-salmon/60 bg-fresh-salmon/15 px-3 py-2 text-xs text-fresh-salmon-dark">
              {notice}
            </p>
          )}

          <ItemPicker
            label="Component (pick another to swap the part, e.g. a colour variant)"
            value={componentId}
            onChange={(it) => {
              setComponentId(it?.id ?? line?.component_item_id ?? null);
              const next = it?.base_uom ?? line?.item?.base_uom ?? null;
              setProducedUom(next);
              if (next) setUom(next);
            }}
            filter={pickerFilter}
          />

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="line-qty">Qty per unit</Label>
              <Input
                id="line-qty"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="line-uom">UoM</Label>
              <UomSelect id="line-uom" value={uom} onChange={setUom} convertibleTo={producedUom} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="line-scrap">Scrap %</Label>
              <Input
                id="line-scrap"
                value={scrap}
                onChange={(e) => setScrap(e.target.value)}
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>
          </div>
          <BomQtyPreview quantity={quantity} uom={uom} producedUom={producedUom} />
          <div className="space-y-1.5">
            <Label htmlFor="line-notes">Notes</Label>
            <Textarea
              id="line-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Assembly instructions, reference designator, torque spec…"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save line'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
